import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  structuredData?: object;
  // Restaurant-specific props for menu pages
  restaurantName?: string;
  restaurantAddress?: string;
  restaurantPhone?: string;
  menuType?: string;
  cuisine?: string;
}

export const SEOHead = ({
  title = "Miam.Food - Menus Digitaux pour Restaurants | Créez vos QR Codes Menu",
  description = "Créez des menus digitaux professionnels pour votre restaurant en 5 minutes. QR codes, analytics, réservations en ligne. Essai gratuit 14 jours. Solution française pour restaurateurs.",
  keywords = "menu digital, qr code restaurant, menu en ligne, carte restaurant numérique, miam.food, menuify, gestion restaurant, menu interactif, analytics restaurant, réservation en ligne",
  canonical = "https://miam.food",
  ogImage = "https://miam.food/og-image.png",
  structuredData,
  restaurantName,
  restaurantAddress,
  restaurantPhone,
  menuType,
  cuisine
}: SEOHeadProps) => {
  
  // Generate restaurant-specific structured data if props are provided
  const restaurantStructuredData = restaurantName ? {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    "name": restaurantName,
    "description": description,
    "address": restaurantAddress ? {
      "@type": "PostalAddress",
      "streetAddress": restaurantAddress,
      "addressCountry": "FR"
    } : undefined,
    "telephone": restaurantPhone,
    "url": canonical,
    "servesCuisine": cuisine || menuType,
    "hasMenu": {
      "@type": "Menu",
      "name": `Menu ${restaurantName}`,
      "description": description,
      "url": canonical
    },
    "image": ogImage,
    "priceRange": "$",
    "acceptsReservations": true
  } : null;
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Miam.Food",
    "description": "Plateforme de création de menus digitaux pour restaurants avec QR codes et analytics",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "19",
      "priceCurrency": "EUR",
      "priceValidUntil": "2025-12-31",
      "availability": "https://schema.org/InStock"
    },
    "provider": {
      "@type": "Organization",
      "name": "Miam.Food",
      "url": "https://miam.food",
      "logo": "https://miam.food/logo.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+33-1-23-45-67-89",
        "contactType": "Customer Service",
        "availableLanguage": "French"
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "127",
      "bestRating": "5"
    }
  };

  return (
    <Helmet>
      {/* Basic SEO */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical} />
      
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Miam.Food" />
      <meta property="og:locale" content="fr_FR" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Additional SEO */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="language" content="French" />
      <meta name="author" content="Miam.Food" />
      <meta name="geo.region" content="FR" />
      <meta name="geo.country" content="France" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || restaurantStructuredData || defaultStructuredData)}
      </script>
    </Helmet>
  );
};